import _toConsumableArray from "/usr/local/micropublication/micropub-public/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/usr/local/micropublication/micropub-public/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useSafeLayoutEffect } from "@chakra-ui/hooks";
import { fromEntries } from "@chakra-ui/utils";
import { createPopper as defaultCreatePopper } from "@popperjs/core";
import { dequal } from "dequal";
import * as React from "react";

function resolve(obj, elements) {
  return function (prev) {
    var next = fromEntries(elements.map(function (element) {
      return [element, obj[element]];
    }));
    var isPopperEqual = dequal(prev.popper, next.popper);
    var isArrowEqual = dequal(prev.arrow, next.arrow);
    if (isArrowEqual && isPopperEqual) return prev;
    return next;
  };
}

var EMPTY_MODIFIERS = [];
export function usePopper(referenceElement, popperElement, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$enabled = _options.enabled,
      enabled = _options$enabled === void 0 ? true : _options$enabled;
  var prevOptions = React.useRef(null);
  var optionsWithDefaults = {
    onFirstUpdate: options.onFirstUpdate,
    placement: options.placement || "bottom",
    strategy: options.strategy || "absolute",
    modifiers: options.modifiers || EMPTY_MODIFIERS
  };

  var _React$useState = React.useState({
    popper: {
      position: optionsWithDefaults.strategy,
      left: "0",
      top: "0"
    }
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      styles = _React$useState2[0],
      setStyles = _React$useState2[1];

  var _React$useState3 = React.useState({}),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      attrs = _React$useState4[0],
      setAttrs = _React$useState4[1];

  var updateStateModifier = React.useMemo(function () {
    return {
      name: "updateState",
      enabled: enabled,
      phase: "write",
      fn: function fn(_ref) {
        var state = _ref.state;
        var elements = Object.keys(state.elements);
        setStyles(resolve(state.styles, elements));
        setAttrs(resolve(state.attributes, elements));
      },
      requires: ["computeStyles"]
    };
  }, [enabled]);
  var popperOptions = React.useMemo(function () {
    var newOptions = {
      onFirstUpdate: optionsWithDefaults.onFirstUpdate,
      placement: optionsWithDefaults.placement,
      strategy: optionsWithDefaults.strategy,
      modifiers: [].concat(_toConsumableArray(optionsWithDefaults.modifiers), [updateStateModifier, {
        name: "applyStyles",
        enabled: false
      }])
    };

    if (dequal(prevOptions.current, newOptions)) {
      return prevOptions.current || newOptions;
    }

    prevOptions.current = newOptions;
    return newOptions;
  }, [optionsWithDefaults.onFirstUpdate, optionsWithDefaults.placement, optionsWithDefaults.strategy, optionsWithDefaults.modifiers, updateStateModifier]);
  var popperInstanceRef = React.useRef();
  useSafeLayoutEffect(function () {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.setOptions(popperOptions);
    }
  }, [popperOptions]);
  useSafeLayoutEffect(function () {
    if (referenceElement == null || popperElement == null) {
      return;
    }

    var createPopper = options.createPopper || defaultCreatePopper;
    var popperInstance = createPopper(referenceElement, popperElement, popperOptions);
    popperInstanceRef.current = popperInstance;
    return function () {
      popperInstance.destroy();
      popperInstanceRef.current = null;
    };
  }, [referenceElement, popperElement, options.createPopper]);
  React.useEffect(function () {
    var id = requestAnimationFrame(function () {
      var _popperInstanceRef$cu;

      (_popperInstanceRef$cu = popperInstanceRef.current) == null ? void 0 : _popperInstanceRef$cu.forceUpdate();
    });
    return function () {
      cancelAnimationFrame(id);
    };
  }, []);
  return {
    state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
    styles: styles,
    attributes: attrs,
    update: popperInstanceRef.current ? popperInstanceRef.current.update : null,
    forceUpdate: popperInstanceRef.current ? popperInstanceRef.current.forceUpdate : null
  };
}